import type { ContextMenuEntry } from "@/components/common/ContextMenu";
import { environmentFileContentUrl, lookupEnvironmentFilesByIds } from "@/lib/api";
import { downloadFile } from "@/lib/download-file";
import { copyPathToClipboard, insertPathIntoMessage } from "@/lib/path-actions";
import { reportSharedVolumeFailure } from "@/lib/shared-volume-failure";
import { sharedVolumeFileId } from "@/lib/shared-volume-files";
import { useChatStore } from "@/stores/useChatStore";
import { useToastStore } from "@/stores/useToastStore";

const NOT_RUNNING_REASON = "エージェント環境が起動しているときだけ開けます。";

/**
 * 共有ボリュームのファイルを読み取り専用のタブで開く。backend に照合し、無ければ知らせる。待つ間にツリーの環境が
 * 変わっていれば (`currentEnvironmentId` が別の環境を返せば) 開かない。
 */
export async function openSharedVolumeFile(
	currentEnvironmentId: () => string | null,
	volumeId: string,
	path: string,
): Promise<void> {
	const environmentId = currentEnvironmentId();
	if (environmentId === null) return;
	const id = sharedVolumeFileId(volumeId, path);
	const [file] = await lookupEnvironmentFilesByIds(environmentId, [id]);
	if (currentEnvironmentId() !== environmentId) return;
	if (!file) {
		useToastStore.getState().show({ type: "error", message: "ファイルが見つかりません。" });
		return;
	}
	const chat = useChatStore.getState();
	chat.mergeOpenEnvironmentFileEntries([file]);
	chat.openFilePreview(file.id);
}

/**
 * 右クリックのメニューのうち、権限を問わない項目 (開く・ダウンロードと、パスをコピー・メッセージにパスを追加)。
 * 当てはまらない項目は出さない。
 */
export function sharedVolumeEntryMenuItems({
	currentEnvironmentId,
	volumeId,
	path,
	openable,
	running,
	pathToCopy,
	canInsertPath,
}: {
	currentEnvironmentId: () => string | null;
	volumeId: string;
	path: string;
	/** 1 件だけを選んでいて、開けるファイルか。 */
	openable: boolean;
	running: boolean;
	/** パスを使える (1 件だけを選んでいる) なら、その環境の中の絶対パス。 */
	pathToCopy: string | null;
	canInsertPath: boolean;
}): ContextMenuEntry[] {
	const items: ContextMenuEntry[] = [];
	if (openable) {
		const notRunning = running ? undefined : NOT_RUNNING_REASON;
		items.push(
			{
				label: "開く",
				testId: "shared-volume-menu-open",
				disabled: !running,
				title: notRunning,
				onSelect: () =>
					reportSharedVolumeFailure(
						openSharedVolumeFile(currentEnvironmentId, volumeId, path),
						"ファイルを開けませんでした。",
					),
			},
			{
				label: "ダウンロード",
				testId: "shared-volume-menu-download",
				disabled: !running,
				title: notRunning,
				onSelect: () => {
					const environmentId = currentEnvironmentId();
					if (environmentId === null) return;
					downloadFile(
						environmentFileContentUrl(environmentId, sharedVolumeFileId(volumeId, path), {
							download: true,
						}),
					);
				},
			},
			{ separator: true },
		);
	}
	if (pathToCopy !== null) {
		items.push({
			label: "パスをコピー",
			testId: "shared-volume-menu-copy-path",
			onSelect: () =>
				reportSharedVolumeFailure(
					copyPathToClipboard(pathToCopy),
					"パスをコピーできませんでした。",
				),
		});
		if (canInsertPath) {
			items.push({
				label: "メッセージにパスを追加",
				testId: "shared-volume-menu-add-path",
				onSelect: () => insertPathIntoMessage(pathToCopy),
			});
		}
	}
	return items;
}
